import React from "react";
import { ScaleLoader } from "react-spinners";
import { Col } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import isEqual from "lodash.isequal";
import classNames from "classnames";
import moment from "moment";

import {
  serviceCaller,
  ServiceCallerProps,
  subscribeToResizeChanges,
  withLoginRedirect,
} from "../../composition";
import { phonePlainFormat } from "../../../utils/helpers";
import {
  getChats,
  searchChats,
  searchChatsByDateRange,
} from "../../../utils/fetch";

import { IChat } from "../../../types/IMessage";
import ChatRow from "../chatRow";

import s from "./styles.scss";

interface Props extends ServiceCallerProps {
  searchText?: string;
  fromDate?: Date;
  toDate?: Date;
  selectedDonorId: string;
  mobileMode: boolean;

  unreadMessages: { [key: string]: number };
  loadMessages: (donorId: string) => void;
}

interface State {
  chats: Array<IChat>;
  selectedDonorId: string;

  page: number;
  hasMore: boolean;
  isLoading: boolean;
  searching: boolean;
  errorObject: any;
}

class ChatList extends React.PureComponent<Props, State> {
  state = {
    chats: [],
    selectedDonorId: this.props.selectedDonorId,

    page: 0,
    hasMore: false,
    isLoading: false,
    searching: false,
    errorObject: null,
  };

  hasDateRangeFilter = (): boolean => {
    return !!(this.props.fromDate || this.props.toDate);
  };

  componentDidMount() {
    if (this.props.config) {
      this.loadChats();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { searchText, selectedDonorId, fromDate, toDate } = this.props;
    const charityId = this.props.currentCharity?.id;
    const prevCharityId = prevProps.currentCharity?.id;

    if (charityId && charityId !== prevCharityId) {
      return;
    }

    if (
      this.state.selectedDonorId !== selectedDonorId &&
      selectedDonorId !== prevProps.selectedDonorId
    ) {
      this.setState({ selectedDonorId });
    }

    if (!isEqual(this.props.config, prevProps.config)) {
      this.loadChats(this.state.page);
    }

    if (this.props.mobileMode != prevProps.mobileMode) {
      this.loadFirstChat();
    }

    if (fromDate !== prevProps.fromDate || toDate !== prevProps.toDate) {
      if (this.hasDateRangeFilter()) {
        this.searchByDateRange();
      } else {
        this.loadChats();
      }
    } else if (searchText !== prevProps.searchText) {
      if (searchText === "") {
        this.loadChats();
      } else {
        this.searchByNumber();
      }
    }
  }

  loadChats = (page: number = 0): void => {
    const {
      config: { notificationServiceUrl },
      currentCharity,
      serviceAccessToken,
    } = this.props;

    this.setState({
      isLoading: true,
      errorObject: null,
      ...(page === 0 ? { chats: [] } : {}),
    });

    getChats(
      notificationServiceUrl,
      currentCharity.id,
      page,
      serviceAccessToken
    )
      .then(response => {
        let filteredResponse = response.content.filter(
          chat => chat && chat.donor
        );
        this.setState({
          chats:
            page == 0
              ? filteredResponse
              : this.state.chats.concat(filteredResponse),
          hasMore: !response.last,
          page,
          isLoading: false,
        });

        this.loadFirstChat();
      })
      .catch(error => {
        this.setState({
          errorObject: error,
          isLoading: false,
        });
      });
  };

  loadFirstChat = (): void => {
    if (this.props.mobileMode && !this.props.selectedDonorId) {
      this.showSelectedChat(
        this.state.chats.length > 0 ? this.state.chats[0] : null
      );
    }
  };

  showSelectedChat = (chat?: IChat): void => {
    this.setState({
      selectedDonorId: chat ? chat.donor.id : "",
    });

    if (chat) {
      this.props.loadMessages(chat.donor.id);
    }
  };

  searchByNumber = (): void => {
    this.setState({
      searching: true,
    });

    searchChats(
      this.props.config.notificationServiceUrl,
      this.props.currentCharity.id,
      this.props.serviceAccessToken,
      phonePlainFormat(this.props.searchText)
    )
      .then(response => {
        this.setState({
          chats: response.filter(chat => chat.donor),
          searching: false,
          hasMore: false,
        });

        this.loadFirstChat();
      })
      .catch(_ => {
        this.setState({
          chats: [],
          searching: false,
          hasMore: false,
        });
      });
  };

  searchByDateRange = (page: number = 0): void => {
    this.setState({
      searching: true,
    });

    const { fromDate, toDate } = this.props;
    const from = fromDate
      ? moment(fromDate)
          .startOf("day")
          .valueOf()
      : undefined;
    const to = toDate
      ? moment(toDate)
          .endOf("day")
          .valueOf()
      : undefined;

    searchChatsByDateRange(
      this.props.config.notificationServiceUrl,
      this.props.currentCharity.id,
      this.props.serviceAccessToken,
      from,
      to,
      page
    )
      .then(response => {
        const filtered = response.content.filter(chat => chat && chat.donor);

        this.setState({
          chats: page == 0 ? filtered : this.state.chats.concat(filtered),
          hasMore: !response.last,
          searching: false,
          isLoading: false,
        });

        this.loadFirstChat();
      })
      .catch(_ => {
        this.setState({
          chats: [],
          searching: false,
          hasMore: false,
          isLoading: false,
        });
      });
  };

  isSelectedChat = (donorId: string): boolean => {
    return donorId !== "" && donorId === this.state.selectedDonorId;
  };

  fetchMoreData = () => {
    const { page, isLoading, hasMore } = this.state;

    if (hasMore && !isLoading) {
      this.setState({ isLoading: true });

      if (this.hasDateRangeFilter()) {
        this.searchByDateRange(page + 1);
      } else {
        this.loadChats(page + 1);
      }
    }
  };

  render() {
    const loader = (
      <div className={s.loader}>
        <ScaleLoader color="#1f65b1" loading={true} />
      </div>
    );

    const { unreadMessages } = this.props;
    const { chats, searching, isLoading } = this.state;
    return (
      <Col sm={12} md={5} className={s.container}>
        <div
          className={classNames(isLoading ? s.scrollLoading : s.scroll)}
          onScroll={this.fetchMoreData}
        >
          {chats.length > 0 &&
            chats.map((chat, index) => (
              <ChatRow
                unreadMessages={
                  unreadMessages ? unreadMessages[chat.donor.id] > 0 : false
                }
                key={"chat_" + index}
                index={index}
                chat={chat}
                onClick={this.showSelectedChat}
                isSelected={this.isSelectedChat(chat.donor.id)}
              />
            ))}
        </div>
        {(isLoading || searching) && loader}
        {chats.length == 0 && !searching && !isLoading && (
          <h5 className={s.noChats}>{"  NO CHATS AVAILABLE "}</h5>
        )}
      </Col>
    );
  }
}

export default serviceCaller(
  withLoginRedirect(withStyles(s)(subscribeToResizeChanges(ChatList)))
);
